/**
 * Network template starters as Seed Registry providers (P2 of the
 * start-intent plan). Each wraps the existing `NETWORK_TEMPLATES` →
 * `hydrateImportedResources` mechanism in a `commands` payload, so a
 * deep link (`seed=sample:network-three-tier-web`) hydrates through the
 * same path the assistant panel's `sourceTemplateId` effect uses.
 *
 * Ids and labels are duplicated here (not imported from
 * networkTemplates.ts) so this module stays out of the initial bundle —
 * the template builders are only loaded when a seed actually runs.
 */

import type { SeedProvider } from '../../../core/seeds/SeedRegistry';
import { coerceProviderDialectId } from '../providers/providerDialectRegistry';

const NETWORK_SEED_DEFS: ReadonlyArray<{ id: string; label: string }> = [
  { id: 'network-three-tier-web', label: 'Three-tier web app' },
  { id: 'network-serverless-api', label: 'Serverless API' },
  { id: 'network-event-driven-platform', label: 'Event-driven platform' },
  { id: 'network-microservices-platform', label: 'Microservices platform' },
  { id: 'network-kubernetes-platform', label: 'Kubernetes platform' },
  { id: 'network-data-platform', label: 'Data and analytics platform' },
  { id: 'network-ai-rag-platform', label: 'AI / RAG application' },
  { id: 'network-vpc-baseline', label: 'Public/private network baseline' },
  { id: 'network-hub-spoke', label: 'Hub-and-spoke network' },
  { id: 'network-hybrid-connectivity', label: 'Hybrid connectivity' },
  { id: 'network-multi-region-resilience', label: 'Multi-region resilience' },
  { id: 'network-edge-delivery', label: 'Edge delivery and DNS' },
  { id: 'network-public-exposure-review', label: 'Public exposure review' },
  { id: 'network-zero-trust-access', label: 'Zero-trust access' },
  { id: 'network-segmentation-zones', label: 'Segmentation and trust zones' },
  { id: 'network-encryption-secrets', label: 'Encryption and secrets baseline' },
  { id: 'network-observability-baseline', label: 'Observability baseline' },
  { id: 'network-cost-allocation', label: 'Cost allocation view' },
  { id: 'network-incident-response', label: 'Incident response topology' },
  { id: 'network-demo-security-review', label: 'Security review demo' },
];

const makeNetworkTemplateSeed = (id: string, label: string): SeedProvider => ({
  id,
  type: 'network',
  label,
  provide: () => ({
    format: 'commands',
    build: async (cm, ctx) => {
      const [{ getNetworkTemplate }, { hydrateImportedResources }] = await Promise.all([
        import('../templates/networkTemplates'),
        import('../interop/hydrateImportedResources'),
      ]);
      const template = getNetworkTemplate(id, {
        dialectId: coerceProviderDialectId(ctx?.dialectId),
      });
      if (!template) return;
      await hydrateImportedResources(template, cm, {
        commandName: `Load ${template.sourceLabel}`,
        doc: ctx?.doc ?? undefined,
      });
    },
  }),
});

export const networkTemplateSeeds: SeedProvider[] = NETWORK_SEED_DEFS.map((d) =>
  makeNetworkTemplateSeed(d.id, d.label),
);
